import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Label,
  Skeleton,
  ToggleGroup,
  ToggleGroupItem,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';
import { enqueueSnackbar } from 'notistack';
import {
  Check,
  ClipboardPaste,
  CopyPlus,
  Copy,
  Eye,
  HistoryIcon,
  Loader2,
  MoreVertical,
  Plus,
  RefreshCw,
  Trash2,
  TriangleAlert,
  XIcon,
} from 'lucide-react';
import {
  TAXONOMY_LIST_QUERY_KEYS,
  taxonomyListDetailQueryOptions,
  taxonomyListBlockingSchemasQueryOptions,
  taxonomyListImpactQueryOptions,
  taxonomyListUsageQueryOptions,
  useAddTaxonomyListVersion,
  useDeleteTaxonomyList,
  useDuplicateTaxonomyList,
  useSetTaxonomyListStatus,
  useUpdateTaxonomyListDraft,
  type TaxonomyListColumn,
  type TaxonomyListDetail,
  type TaxonomyListRow,
  type TaxonomyListVersion,
  listColumns,
  withValueAndCode,
  type ListColumns,
} from '@/entities/taxonomy-list';
import { clampToLength, MAX_LIST_CELL_LENGTH } from '@/entities/taxonomy';
import { useCurrentUser, usePermissions } from '@/entities/user';
import { parseProblemDetail } from '@/shared/utils/problem-details';
import { FormDialog } from '@/shared/ui/FormDialog';
import { TaxonomyReasonModal } from '../TaxonomyList/components/TaxonomyReasonModal/TaxonomyReasonModal';
import { VersionMenu } from '../TaxonomyDetail/components/VersionMenu/VersionMenu';
import { BulkPasteModal } from './components/BulkPasteModal/BulkPasteModal';
import { FillEmptyCellsModal } from './components/FillEmptyCellsModal/FillEmptyCellsModal';
import { fillEmptyCells } from './lib/fillEmptyCells';
import { ListHistoryDrawer } from './components/ListHistoryDrawer/ListHistoryDrawer';
import { ListImpactSummary } from './components/ListImpactSummary/ListImpactSummary';
import { rowCodeWithSpace } from './lib/codeWithSpace';
import { parseTabular } from './lib/parseTabular';
import { ProblemTriangle } from '../components/ProblemTriangle/ProblemTriangle';

const AUTOSAVE_MS = 800;

const ROW_HEIGHT = 37; // h-9 cell (36px) + 1px bottom border
// The threshold the schema grid already settled on (FieldsEditor).
const VIRTUALIZE_THRESHOLD = 50;
/**
 * Every write into a row clamps, because three funnels reach one: the cell
 * input, an inline block paste, and the paste modal. Only the first passes
 * through an element `maxLength` could guard, and rows are stored as jsonb and
 * re-read on every schema render that binds the list.
 */
const clampListCell = (v: string) => clampToLength(v, MAX_LIST_CELL_LENGTH);
const clampListRow = (r: TaxonomyListRow): TaxonomyListRow => ({
  ...r,
  cells: Object.fromEntries(Object.entries(r.cells).map(([k, v]) => [k, clampListCell(v)])),
});

interface DraftState {
  name: string;
  description: string;
  columns: TaxonomyListColumn[];
  rows: TaxonomyListRow[];
}

/**
 * Ids for the Value or Code column a read-only version lacks. Stable across renders, so the
 * placeholder cells keep their keys; nothing is ever saved under them.
 */
function placeholderColumnIds() {
  let n = 0;
  return () => `missing-column-${++n}`;
}

/** The version a write lands on: the current one, else the highest numbered. */
function resolveHead(versions: TaxonomyListVersion[], currentVersionId: string | null) {
  return (
    versions.find((v) => v.id === currentVersionId) ??
    [...versions].sort((a, b) => b.versionNumber - a.versionNumber)[0]
  );
}

export interface TaxonomyListEditorProps {
  listId: string;
  /** Where the sheet draws its header bar. The editor portals its bar there. */
  barSlot: HTMLElement | null;
  onClose: () => void;
  /** Opens another list in the same sheet: the copy a Duplicate made. */
  onOpenList: (listId: string) => void;
}

/**
 * Editor for one shared vocabulary. A list is a small TABLE — N named columns
 * (typically a display Value and an output Code) and rows of cells — so a single
 * vocabulary can feed different fields different columns. The editor labels two of them
 * Value and Code; any others follow them, editable cell by cell but never added, removed or
 * renamed, because a reader such as the ad format catalogue finds them by name.
 *
 * Mirrors the schema builder's editing model: the head DRAFT version is edited
 * in place with debounced autosave; published versions are frozen and require
 * branching a new version, which keeps any field pinned to the old snapshot safe.
 */
export function TaxonomyListEditor({
  listId,
  barSlot,
  onClose,
  onOpenList,
}: TaxonomyListEditorProps) {
  const { t } = useTranslation('knowledge');
  const nameId = useId();
  const descriptionId = useId();

  const queryClient = useQueryClient();
  const remove = useDeleteTaxonomyList();
  // Once this list is deleted the sheet is closing over it; asking for it again only earns 404s.
  const listExists = !remove.isSuccess;
  const query = useQuery({ ...taxonomyListDetailQueryOptions(listId), enabled: listExists });
  const usageQuery = useQuery({ ...taxonomyListUsageQueryOptions(listId), enabled: listExists });
  const currentUser = useCurrentUser();
  const { canManageTaxonomy } = usePermissions();
  const list = query.data;

  const head = useMemo(
    () => (list ? resolveHead(list.versions, list.currentVersionId) : undefined),
    [list],
  );

  const isDraft = head?.status === 'draft';

  const [draft, setDraft] = useState<DraftState | null>(null);
  const [saving, setSaving] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [fillOpen, setFillOpen] = useState(false);
  const [lastFillCount, setLastFillCount] = useState<number | null>(null);
  const [publishOpen, setPublishOpen] = useState(false);
  /** True while the pre-publish autosave flush is in flight. */
  const [publishing, setPublishing] = useState(false);
  const [newVersionOpen, setNewVersionOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  // Which version is being VIEWED (null = the head). Browsing past versions
  // read-only is what makes a schema's PINNED vocabulary inspectable — we pin
  // on publish, so without this the pin points at something unreadable.
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [restoreVersionId, setRestoreVersionId] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  /**
   * Set when the server rejects a save because someone else got there first.
   *
   * Autosave stops while this is set — every retry would carry the same stale
   * snapshot of the whole vocabulary. The local edits stay on screen so nothing
   * disappears before the user has decided what to do with them.
   */
  const [conflict, setConflict] = useState<string | null>(null);
  /**
   * A save the server refused for a reason that is NOT a conflict — a 500, a
   * dropped connection.
   *
   * Its own state rather than a flavour of `conflict`, because the honest
   * action is the opposite one. A 409 means someone else's work is on the
   * server and the only safe move is to take theirs; this means the server
   * took nothing, so resending the same snapshot is safe and is what the user
   * wants. Without it the header could only choose between claiming everything
   * was saved and spinning forever on a request that is not in flight.
   */
  const [saveError, setSaveError] = useState<string | null>(null);

  // Read by the publish and restore dialogs.
  const impactQuery = useQuery({ ...taxonomyListImpactQueryOptions(listId), enabled: listExists });
  // The delete dialog asks what the server's delete guard counts, published versions included.
  const blockersQuery = useQuery({
    ...taxonomyListBlockingSchemasQueryOptions(listId),
    enabled: deleteOpen && listExists,
  });
  const blockers = blockersQuery.data ?? [];

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seededFor = useRef<string | null>(null);

  const update = useUpdateTaxonomyListDraft(listId, head?.id ?? '');
  const addVersion = useAddTaxonomyListVersion(listId);
  const setStatus = useSetTaxonomyListStatus(listId, head?.id ?? '');
  const duplicate = useDuplicateTaxonomyList();
  /** True from the Duplicate click until the copy request is sent, which waits on a flush. */
  const [duplicating, setDuplicating] = useState(false);

  /**
   * Seeds the local draft once per head version, and again when the cache carries a newer
   * revision of it while nothing here is unsaved. That newer revision can be this list's own
   * last edit, flushed by the editor of a sheet closed a moment ago and landing after this one
   * seeded: without the reseed the next save would send the older snapshot under the newer
   * revision and silently undo it.
   */
  const seedDraft = useCallback((from: TaxonomyListDetail, version: TaxonomyListVersion) => {
    seededFor.current = version.id;
    revisionRef.current = version.revision;
    setDraft({
      name: from.name,
      description: from.description ?? '',
      columns: withValueAndCode(version.columns),
      rows: version.rows,
    });
    // The save state belonged to the version we just replaced. Left standing,
    // a failed save followed by "New version" reseeded a draft nobody had
    // edited yet while `dirtyRef` and `saving` were still true — so an
    // untouched draft claimed to be saving, forever, with nothing to send.
    dirtyRef.current = false;
    setSaving(false);
    setSaveError(null);
  }, []);

  useEffect(() => {
    if (!list || !head) return;
    if (seededFor.current === head.id) {
      const newerOnServer = head.revision > revisionRef.current;
      if (!newerOnServer || dirtyRef.current || savingRef.current) return;
    }
    seedDraft(list, head);
  }, [list, head, seedDraft]);

  const { mutateAsync: saveAsync } = update;

  /**
   * The revision the draft is based on: set when it is seeded and from each save's response,
   * never from the cache alone. A cache revision this editor did not produce comes with content
   * the draft does not have, so sending it would overwrite that content instead of getting a 409.
   */
  const revisionRef = useRef(1);

  /**
   * Mirror of `draft` so `edit` can compute the next state without a state
   * updater, and so two `edit` calls in one handler still compose (the ref is
   * advanced immediately, whereas a re-render is a tick away). The save loop
   * reads it too, which is how an edit made mid-flight reaches the next pass.
   */
  const draftRef = useRef<DraftState | null>(null);
  draftRef.current = draft;

  // Read inside the save loop rather than captured in its closure: the loop
  // outlives the render that started it, so a stale `isDraft`/`conflict` would
  // let it write into a version that has since been published.
  const conflictRef = useRef<string | null>(null);
  conflictRef.current = conflict;
  const isDraftRef = useRef(false);
  isDraftRef.current = isDraft;
  const dirtyRef = useRef(false);
  /** Whether any save from this editor landed, so closing it refreshes the lists index. */
  const savedRef = useRef(false);

  /**
   * Persists the current draft, with at most ONE save in flight.
   *
   * The server increments `revision` on every write and rejects a stale one, so
   * a second save fired before the first response landed carries the same
   * number, comes back 409, and latches the conflict banner on someone editing
   * alone — after which autosave is dead for the session and the only way out
   * discards their edits. Every save ships the whole rows array and the reply is
   * the whole list, so overtaking the 800ms debounce is the ordinary case for a
   * seeded 250-row vocabulary, not an edge one.
   *
   * Resolves to whether everything drained. A caller cannot ask `conflict`
   * instead: it is state and has not re-rendered yet, and a non-409 failure
   * leaves it null while the edit is still unsent.
   */
  const savingRef = useRef<Promise<boolean> | null>(null);
  const flushSave = useCallback(async (): Promise<boolean> => {
    // Already running: its loop will pick up whatever is dirty now.
    if (savingRef.current) return savingRef.current;

    const loop = (async () => {
      while (dirtyRef.current && isDraftRef.current && !conflictRef.current) {
        const snapshot = draftRef.current;
        if (!snapshot) break;
        // Cleared BEFORE the request, not after: an edit arriving while it is in
        // flight sets it again, and the loop takes another pass for that edit.
        dirtyRef.current = false;
        try {
          const updated = await saveAsync({
            expectedRevision: revisionRef.current,
            // An emptied name is not sent, so the saved one stands while the field says it is
            // required.
            name: snapshot.name.trim() || undefined,
            description: snapshot.description.trim(),
            columns: snapshot.columns,
            rows: snapshot.rows,
          });
          // Taken from the RESPONSE, not from a re-render: the next iteration
          // may run before React has re-rendered with the updated cache, and
          // sending the previous revision would 409 against our own save.
          const savedHead = resolveHead(updated.versions, updated.currentVersionId);
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- resolveHead falls back to sort()[0], undefined for a list with no versions
          if (savedHead) revisionRef.current = savedHead.revision;
          savedRef.current = true;
        } catch (error) {
          // The snapshot never landed, so it is still unsaved.
          dirtyRef.current = true;
          const { status, detail } = parseProblemDetail(
            error,
            t('taxonomy.taxonomyListDetail.yourChangesCouldNotSaved'),
          );
          // Autosave stops on a 409: every retry carries the same stale snapshot
          // and would try to overwrite the other editor again. The local edits
          // stay on screen until the user decides.
          //
          // Anything else is reported instead of latched. `saveError` is
          // deliberately NOT part of the loop condition above: a transient blip
          // must not disable autosave for the session, so the next edit re-arms
          // the timer and tries again on its own.
          if (status === 409) setConflict(detail);
          else setSaveError(detail);
          break;
        }
      }
      const fullyDrained = !dirtyRef.current;
      // Only a fully drained loop means there is nothing left to send. Clearing
      // this unconditionally in the `finally` reported "All changes saved" over
      // an edit that never left the browser, because a non-409 failure re-arms
      // `dirtyRef` and breaks out without setting `conflict`.
      if (fullyDrained) {
        setSaving(false);
        setSaveError(null);
      }
      return fullyDrained;
    })();

    savingRef.current = loop;
    try {
      const drained = await loop;
      return drained;
    } finally {
      savingRef.current = null;
    }
  }, [saveAsync, t]);

  const scheduleSave = useCallback(() => {
    if (!isDraftRef.current || conflictRef.current) return;
    dirtyRef.current = true;
    setSaving(true);
    // A fresh edit supersedes the last failure, so the header must not keep
    // offering to retry a snapshot the user has already moved past.
    setSaveError(null);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      timer.current = null;
      void flushSave();
    }, AUTOSAVE_MS);
  }, [flushSave]);

  /**
   * Sends whatever is still pending when this screen goes away.
   *
   * Autosave is a TRAILING debounce, so cancelling the timer on unmount without
   * flushing discards the last edit rather than sending it — and every cell here
   * is a controlled input that commits per keystroke, so "type, then close the
   * sheet" is enough to lose one.
   *
   * `flushSave` is read through a ref so its identity cannot re-run this and
   * flush mid-typing.
   */
  const flushRef = useRef(flushSave);
  flushRef.current = flushSave;
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
      // Also waits on a save already in flight. A draft save only marks the lists index stale,
      // and that page stays mounted under the sheet, so it refetches here, after the last save.
      void flushRef.current().finally(() => {
        if (savedRef.current) {
          void queryClient.invalidateQueries({ queryKey: TAXONOMY_LIST_QUERY_KEYS.list() });
        }
      });
    },
    [queryClient],
  );

  /**
   * Abandons the local edits and reloads whatever the server now holds.
   *
   * A real merge would need per-cell edits rather than whole-snapshot saves, so
   * the honest options are "keep theirs" or "copy mine out by hand" — this is
   * the first. `seededFor` is cleared so the seed effect re-runs against the
   * refetched version instead of keeping the stale draft on screen.
   */
  const reloadAfterConflict = async () => {
    if (timer.current) clearTimeout(timer.current);
    seededFor.current = null;
    dirtyRef.current = false;
    setConflict(null);
    setSaveError(null);
    setSaving(false);
    // Seeded from the reply directly: when the cache already held this revision, the refetch
    // returns an equal object, the effect has nothing to react to, and the rejected draft would
    // stay on screen claiming to be saved.
    const { data } = await query.refetch();
    const fresh = data && resolveHead(data.versions, data.currentVersionId);

    if (data && fresh) seedDraft(data, fresh);
  };

  const edit = useCallback(
    (patch: Partial<DraftState>) => {
      const prev = draftRef.current;
      if (!prev) return;
      const next = { ...prev, ...patch };
      draftRef.current = next;
      // Next value computed in the closure, side effect fired outside the
      // updater, `setDraft` called with a non-function argument — a state
      // updater must be pure (CLAUDE.md). Scheduling the save inside the
      // updater ran it twice per edit under StrictMode, and the impurity was
      // the bug whether or not StrictMode surfaced it.
      setDraft(next);
      scheduleSave();
    },
    [scheduleSave],
  );

  const addRow = () =>
    draft && edit({ rows: [...draft.rows, { id: crypto.randomUUID(), cells: {} }] });

  const removeRow = (rowId: string) =>
    draft && edit({ rows: draft.rows.filter((r) => r.id !== rowId) });

  const setCell = (rowId: string, columnId: string, value: string) =>
    draft &&
    edit({
      rows: draft.rows.map((r) =>
        r.id === rowId ? { ...r, cells: { ...r.cells, [columnId]: clampListCell(value) } } : r,
      ),
    });

  const applyFill = (sourceColumnId: string, targetColumnIds: string[]) => {
    if (!draft) return;
    const result = fillEmptyCells(draft.rows, sourceColumnId, targetColumnIds, clampListCell);
    if (result.filled > 0) edit({ rows: result.rows });
    setLastFillCount(result.filled);
    setFillOpen(false);
  };

  /** Bulk apply from the paste modal — replaces or appends in ONE edit. */
  const applyPastedRows = (rows: TaxonomyListRow[], mode: 'replace' | 'append') =>
    draft &&
    edit({
      rows:
        mode === 'replace' ? rows.map(clampListRow) : [...draft.rows, ...rows.map(clampListRow)],
    });

  /**
   * Inline paste. Onto Value or Code, a multi-cell block spills right and down from that cell,
   * growing the row count as needed, and writes only those two columns. Onto any other column, one
   * value lands in that cell, a spreadsheet's trailing newline included, and a larger block is
   * refused. A plain value with no tab or newline falls through to the normal input paste.
   */
  const handleCellPaste = (
    e: React.ClipboardEvent<HTMLInputElement>,
    rowIndex: number,
    columnId: string,
  ) => {
    if (!draft || !isDraft) return;
    const text = e.clipboardData.getData('text/plain');
    if (!text || (!text.includes('\t') && !text.includes('\n'))) return;

    e.preventDefault();
    // Tabs only: a copied cell like "Korea, Republic of" has no tab and is one value. Leading and
    // inner blank rows are kept so a copied column with a gap stays aligned, and that empty cell
    // clears its target; trailing blank rows are dropped (see parseTabular).
    const grid = parseTabular(text, { delimiter: '\t', keepBlankRows: true });
    if (grid.length === 0) return;

    const pair = listColumns(draft.columns);
    const valueAndCode = [pair.value, pair.code];
    const startColumn = valueAndCode.findIndex((c) => c?.id === columnId);
    if (startColumn === -1) {
      const single = grid.length === 1 && grid[0].length === 1;
      if (!single) {
        enqueueSnackbar(t('taxonomy.taxonomyListDetail.pasteBlockOnlyValueAndCode'), {
          variant: 'warning',
          preventDuplicate: true,
        });
        return;
      }
      const row = draft.rows.at(rowIndex);
      if (row) setCell(row.id, columnId, grid[0][0].trim());
      return;
    }

    const rows = [...draft.rows];
    grid.forEach((cells, r) => {
      const targetIndex = rowIndex + r;
      // Grow the table when the pasted block runs past the last row.
      if (!rows[targetIndex]) rows[targetIndex] = { id: crypto.randomUUID(), cells: {} };
      const target = { ...rows[targetIndex], cells: { ...rows[targetIndex].cells } };
      cells.forEach((value, c) => {
        const column = valueAndCode.at(startColumn + c);
        if (column) target.cells[column.id] = clampListCell(value.trim());
      });
      rows[targetIndex] = target;
    });
    edit({ rows });
    // Blank cells past Code do not count: nothing in them is left out.
    const leftOut = grid.some((cells) =>
      cells.slice(valueAndCode.length - startColumn).some((cell) => cell.trim() !== ''),
    );
    if (leftOut) {
      enqueueSnackbar(t('taxonomy.taxonomyListDetail.pasteExtraColumnsLeftOut'), {
        variant: 'warning',
        preventDuplicate: true,
      });
    }
  };

  /**
   * Publishes the head draft, AFTER sending whatever autosave still owes.
   *
   * The debounce is trailing with no flush, and the armed timer fires
   * regardless of status, so publishing first turns a pending save from late
   * into impossible: the PATCH lands on a version that is no longer a draft,
   * comes back 409 "Only draft list versions can be edited", and this editor
   * reports that as another user's edit and stops autosaving for the session.
   * Flushing makes "Publish" mean "publish what I am looking at".
   */
  const publishDraft = async (reason: string) => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    setPublishing(true);
    const drained = await flushSave().finally(() => setPublishing(false));
    // Freezing a version whose last edit never landed would publish something
    // other than what is on screen, and after publishing that edit can never be
    // saved. The banner (409) or the save toast (anything else) has said why.
    if (!drained) return;
    setStatus.mutate({ status: 'published', reason }, { onSuccess: () => setPublishOpen(false) });
  };

  /**
   * The server copies what it has, so the last edit is sent first, and nothing is copied while it
   * cannot be. Opening the copy rides on this call rather than the hook: a per-call callback stops
   * once the editor unmounts, and the sheet ignores an open request that arrives after it closed.
   */
  function duplicateList() {
    if (duplicating) return;
    setDuplicating(true);
    void flushSave()
      .then((drained) => {
        if (!drained) {
          enqueueSnackbar(t('taxonomy.taxonomyListDetail.duplicateNeedsSave'), {
            variant: 'warning',
          });
          return;
        }
        duplicate.mutate(listId, { onSuccess: (created) => onOpenList(created.id) });
      })
      .finally(() => setDuplicating(false));
  }

  const closeButton = (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon-mini"
          onClick={onClose}
          aria-label={t('taxonomy.taxonomyListDetail.close')}
        >
          <XIcon className="size-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{t('taxonomy.taxonomyListDetail.close')}</TooltipContent>
    </Tooltip>
  );

  // The sheet hides its corner close for this bar, so every state draws one, in the same place,
  // so the focused Close survives the list arriving.
  function renderBar(status: React.ReactNode, controls: React.ReactNode) {
    return (
      barSlot &&
      createPortal(
        <div className="flex w-full min-w-0 items-center gap-sm">
          {status}
          <div className="ml-auto flex shrink-0 items-center gap-xs">
            {controls}
            {closeButton}
          </div>
        </div>,
        barSlot,
      )
    );
  }

  const loadingView = (
    <TooltipProvider>
      {renderBar(null, null)}
      <div className="flex flex-col gap-md" data-testid="taxonomy-list-loading">
        <span className="sr-only">{t('taxonomy.taxonomyListDetail.loading')}</span>
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    </TooltipProvider>
  );

  if (!listExists) {
    return <TooltipProvider>{renderBar(null, null)}</TooltipProvider>;
  }

  if (query.isLoading) return loadingView;

  /**
   * Split from the loading branch deliberately. `retry` is false app-wide and
   * react-query's `throwOnError` is not set here, so a 404 or a 403 leaves
   * `isPending` false and `list` undefined — which the single combined condition
   * rendered as "Loading…" forever. The schema builder splits the same way.
   *
   * A load with no data lands here, and so does a 404 on a refetch: the list was deleted
   * elsewhere, so nothing on screen can be saved. Any other failed refetch keeps the list in the
   * cache, and the editor stays up with the draft on screen.
   *
   * `draft` is deliberately NOT part of this condition: it is seeded by an
   * effect, so on a warm cache the first committed frame of a SUCCESSFUL load
   * has `list` and `head` but no `draft` yet, and folding that in rendered the
   * error view — with a "Try again" button — over a load that was about to work.
   */
  const notFound = parseProblemDetail(query.error, '').status === 404;
  if (!list || !head || notFound) {
    return (
      <TooltipProvider>
        {renderBar(null, null)}
        <div className="py-10 text-center text-sm text-muted-foreground">
          {notFound ? (
            t('taxonomy.taxonomyListDetail.listNotFound')
          ) : (
            <>
              {t('taxonomy.taxonomyListDetail.couldnTLoadList')}{' '}
              <button
                type="button"
                className="text-primary underline"
                onClick={() => void query.refetch()}
              >
                {t('taxonomy.taxonomyListDetail.tryAgain')}
              </button>
            </>
          )}
        </div>
      </TooltipProvider>
    );
  }

  // The one frame between a resolved query and the seed effect running.
  if (!draft) return loadingView;

  const usage = usageQuery.data ?? 0;

  // The version on screen; only the head draft is editable.
  const selected = list.versions.find((v) => v.id === selectedVersionId) ?? head;
  const viewingHeadDraft = selected.id === head.id && isDraft;
  /** Editable AND allowed to edit — see the schema builder's note on the split. */
  const canEdit = viewingHeadDraft && canManageTaxonomy;
  const isViewingOlder = selected.id !== head.id;
  // Read from the selected snapshot when browsing history, from the live draft
  // when editing the head.
  const view = viewingHeadDraft
    ? draft
    : {
        name: list.name,
        description: list.description ?? '',
        columns: withValueAndCode(selected.columns, placeholderColumnIds()),
        rows: selected.rows,
      };
  const columns = listColumns(view.columns);
  const valueAndCodeColumns = [columns.value, columns.code].filter(
    (c): c is TaxonomyListColumn => c !== undefined,
  );
  const nameMissing = canEdit && !view.name.trim();
  const spacedCodeCount = view.rows.filter((row) => rowCodeWithSpace(row, columns)).length;
  function columnLabel(column: TaxonomyListColumn) {
    if (column === columns.value) return t('taxonomy.taxonomyListDetail.valueColumn');
    if (column === columns.code) return t('taxonomy.taxonomyListDetail.codeColumn');
    return column.name;
  }
  const labelledValueAndCode = valueAndCodeColumns.map((c) => ({ ...c, name: columnLabel(c) }));

  const headerBar = renderBar(
    canEdit && (
      <span className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
        {/* Never claim "saved" while a save has been rejected — that
              exact lie is what let concurrent edits disappear quietly. */}
        {conflict ? (
          <span className="flex items-center gap-1.5 font-medium text-destructive">
            <TriangleAlert className="size-3.5" />
            {t('taxonomy.taxonomyListDetail.notSaved')}
          </span>
        ) : saveError ? (
          /* The chip IS the affordance. A 409 gets a banner because it
               needs explaining and its action discards work; this needs
               neither — the server took nothing, so resending is safe
               and the only thing to say is "try again". */
          <button
            type="button"
            onClick={() => {
              setSaveError(null);
              void flushSave();
            }}
            title={saveError}
            className="flex items-center gap-1.5 font-medium text-destructive underline-offset-2 hover:underline"
          >
            <TriangleAlert className="size-3.5" />
            {t('taxonomy.taxonomyListDetail.notSaved')}
            <span aria-hidden>·</span>
            <RefreshCw className="size-3.5" />
            {t('taxonomy.taxonomyListDetail.retrySave')}
          </button>
        ) : saving ? (
          <>
            <Loader2 className="size-3.5 animate-spin" /> {t('taxonomy.taxonomyListDetail.saving')}
          </>
        ) : (
          <>
            <Check className="size-3.5" /> {t('taxonomy.taxonomyListDetail.allChangesSaved')}
          </>
        )}
      </span>
    ),
    <>
      {/* No `onCompare`: a list version differs from its parent by
            columns and rows, and nothing diffs those. Pointing the control
            at the History drawer made a button that named one thing and
            did another. */}
      <VersionMenu
        versions={list.versions}
        selectedId={selected.id}
        headId={head.id}
        canCreate={!isDraft}
        canManage={canManageTaxonomy}
        onSelect={(id) => setSelectedVersionId(id === head.id ? null : id)}
        onCreateVersion={() => setNewVersionOpen(true)}
        onOpenHistory={() => setHistoryOpen(true)}
        onRestore={canManageTaxonomy ? setRestoreVersionId : undefined}
      />
      {canEdit && (
        <Button size="mini" onClick={() => setPublishOpen(true)}>
          {t('taxonomy.taxonomyListDetail.publish')}
        </Button>
      )}
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon-mini"
                aria-label={t('taxonomy.taxonomyListDetail.listActions')}
              >
                <MoreVertical className="size-4" />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent>{t('taxonomy.taxonomyListDetail.listActions')}</TooltipContent>
        </Tooltip>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => setHistoryOpen(true)}>
            <HistoryIcon className="size-4" />
            {t('taxonomy.taxonomyListDetail.history')}
          </DropdownMenuItem>
          {/* Duplicate and Delete write, so a viewer gets History alone. */}
          {canManageTaxonomy && (
            <DropdownMenuItem
              disabled={duplicating || duplicate.isPending}
              onSelect={duplicateList}
            >
              <Copy className="size-4" />
              {t('taxonomy.taxonomyListDetail.duplicate')}
            </DropdownMenuItem>
          )}
          {canManageTaxonomy && (
            <DropdownMenuItem variant="destructive" onSelect={() => setDeleteOpen(true)}>
              <Trash2 className="size-4" />
              {t('taxonomy.taxonomyListDetail.deleteList')}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </>,
  );

  return (
    <TooltipProvider>
      {headerBar}

      <div className="flex min-h-0 flex-1 flex-col gap-md">
        {/* Concurrent-edit notice. A persistent banner rather than a toast:
            autosave has stopped, the edits on screen are unsaved, and that's a
            state the user has to act on — not a message that scrolls away. */}
        {conflict && (
          <div className="flex flex-wrap items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/10 p-3">
            <TriangleAlert className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden />
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <span className="text-sm font-medium text-destructive">
                {t('taxonomy.taxonomyListDetail.someoneElseEditedListYour')}
              </span>
              <span className="text-xs text-muted-foreground">
                {conflict} {t('taxonomy.taxonomyListDetail.savingPausedSoYourCopy')}
              </span>
            </div>
            <Button variant="outline" size="mini" onClick={() => void reloadAfterConflict()}>
              <RefreshCw className="size-3.5" />
              {t('taxonomy.taxonomyListDetail.reloadLatest')}
            </Button>
          </div>
        )}

        {isViewingOlder ? (
          /* Browsing history. This is the view a schema's PINNED binding
             points at, so it has to be reachable and clearly labelled. */
          <p className="flex items-center gap-1.5 rounded-md bg-surface px-3 py-2 text-xs text-muted-foreground">
            <Eye className="size-3.5 shrink-0" aria-hidden />
            {t('taxonomy.taxonomyListDetail.viewingV')}
            {selected.versionNumber} {t('taxonomy.taxonomyListDetail.pastVersionReadOnlySchemas')}
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">
            {usage > 0
              ? t('taxonomy.taxonomyListDetail.usedBySchemas', { count: usage })
              : t('taxonomy.taxonomyListDetail.notYetUsedAnySchema')}
            {/* Names creating a new version as the way to edit, so it is
                only true for someone who could create one. */}
            {!viewingHeadDraft &&
              canManageTaxonomy &&
              t('taxonomy.taxonomyListDetail.versionPublishedReadOnlyCreate')}
            {!viewingHeadDraft && canManageTaxonomy && (
              <Button
                variant="outline"
                size="mini"
                className="ml-2 h-7 text-xs"
                onClick={() => setNewVersionOpen(true)}
              >
                {t('taxonomy.taxonomyListDetail.createNewVersion')}
              </Button>
            )}
          </p>
        )}

        <div className="flex flex-col gap-1.5">
          <Label htmlFor={nameId}>
            {t('taxonomy.createListModal.listName')} <span className="text-destructive">*</span>
          </Label>
          <Input
            id={nameId}
            value={view.name}
            disabled={!canEdit}
            onChange={(e) => edit({ name: e.target.value })}
            placeholder={t('taxonomy.createListModal.eGRegions')}
            maxLength={255}
            aria-invalid={nameMissing || undefined}
            className="bg-input"
          />
          {nameMissing && (
            <span role="alert" className="text-xs text-destructive">
              {t('taxonomy.taxonomyListDetail.listNameRequired')}
            </span>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor={descriptionId}>{t('taxonomy.createListModal.descriptionOptional')}</Label>
          <Input
            id={descriptionId}
            value={view.description}
            disabled={!canEdit}
            onChange={(e) => edit({ description: e.target.value })}
            placeholder={t('taxonomy.createListModal.whatVocabularyCovers')}
            maxLength={1000}
            className="bg-input"
          />
        </div>

        {/* Shown, never editable: the API sets a list's scope once, at creation. */}
        <div className="flex flex-col gap-1.5">
          <Label id={`${nameId}-scope`}>{t('taxonomy.createListModal.scope')}</Label>
          <ToggleGroup
            aria-labelledby={`${nameId}-scope`}
            type="single"
            value={list.scope}
            disabled
            variant="outline"
            size="sm"
            className="grid w-full grid-cols-2"
          >
            {/* eslint-disable-next-line i18next/no-literal-string -- `value` is the TaxonomyScope wire enum */}
            <ToggleGroupItem value="organization">
              {t('taxonomy.createListModal.organization')}
            </ToggleGroupItem>
            {/* eslint-disable-next-line i18next/no-literal-string -- `value` is the TaxonomyScope wire enum */}
            <ToggleGroupItem value="workspace">
              {t('taxonomy.createListModal.workspace')}
            </ToggleGroupItem>
          </ToggleGroup>
          <span className="text-xs text-muted-foreground">
            {t('taxonomy.createListModal.organizationListsReusableEveryWorkspace')}
          </span>
        </div>

        <section
          aria-labelledby={`${nameId}-options`}
          className="flex min-h-64 flex-1 flex-col gap-1.5"
        >
          <h3 id={`${nameId}-options`} className="text-sm font-medium">
            {t('taxonomy.taxonomyListDetail.options')}
          </h3>
          <p role="status" className="text-xs text-warning-foreground empty:sr-only">
            {spacedCodeCount > 0 &&
              t('taxonomy.taxonomyListDetail.codesWithSpace', { count: spacedCodeCount })}
          </p>
          <ListRowsTable
            columns={columns}
            rows={view.rows}
            editable={canEdit}
            onSetCell={setCell}
            onRemoveRow={removeRow}
            onCellPaste={handleCellPaste}
            columnLabel={columnLabel}
          />

          {canEdit && (
            <div className="flex flex-wrap items-center gap-xs">
              <Button variant="ghost" size="mini" className="h-8 text-xs" onClick={addRow}>
                <Plus className="size-3.5" />
                {t('taxonomy.taxonomyListDetail.addOption')}
              </Button>
              <Button
                variant="ghost"
                size="mini"
                className="h-8 text-xs"
                onClick={() => setPasteOpen(true)}
              >
                <ClipboardPaste className="size-3.5" />
                {t('taxonomy.taxonomyListDetail.pasteOptions')}
              </Button>
              <Button
                variant="ghost"
                size="mini"
                className="h-8 text-xs"
                onClick={() => {
                  setLastFillCount(null);
                  setFillOpen(true);
                }}
              >
                <CopyPlus className="size-3.5" />
                {t('taxonomy.taxonomyListDetail.fillEmptyCodes')}
              </Button>
              {lastFillCount !== null && (
                <span role="status" className="text-xs text-muted-foreground">
                  {t('taxonomy.taxonomyListDetail.filledEmptyCells', { count: lastFillCount })}
                </span>
              )}
            </div>
          )}
        </section>
      </div>

      {fillOpen && (
        <FillEmptyCellsModal
          columns={labelledValueAndCode}
          onClose={() => setFillOpen(false)}
          onApply={applyFill}
        />
      )}

      {pasteOpen && (
        <BulkPasteModal
          open
          columns={labelledValueAndCode}
          storedNames={valueAndCodeColumns.map((c) => c.name)}
          existingRowCount={view.rows.length}
          allowReplace={columns.extras.length === 0}
          onClose={() => setPasteOpen(false)}
          onApply={applyPastedRows}
        />
      )}

      <TaxonomyReasonModal
        open={publishOpen}
        title={t('taxonomy.taxonomyListDetail.publishListTitle', { name: list.name })}
        description={t('taxonomy.taxonomyListDetail.publishingFreezesVersionSchemasFollow')}
        extra={<ListImpactSummary impact={impactQuery.data} isLoading={impactQuery.isLoading} />}
        confirmLabel={t('taxonomy.taxonomyListDetail.publish')}
        isSubmitting={setStatus.isPending || publishing}
        onCancel={() => setPublishOpen(false)}
        onConfirm={(reason) => void publishDraft(reason)}
      />

      <TaxonomyReasonModal
        open={newVersionOpen}
        title={t('taxonomy.taxonomyListDetail.newVersion')}
        description={t('taxonomy.taxonomyListDetail.branchesEditableDraftCurrentVersion')}
        confirmLabel={t('taxonomy.taxonomyListDetail.createVersion')}
        isSubmitting={addVersion.isPending}
        onCancel={() => setNewVersionOpen(false)}
        onConfirm={(reason) =>
          addVersion.mutate(
            { expectedVersion: list.version, reason },
            {
              onSuccess: () => {
                setNewVersionOpen(false);
                // Jump to the new head so editing continues there.
                setSelectedVersionId(null);
              },
            },
          )
        }
      />

      {restoreVersionId &&
        (() => {
          const target = list.versions.find((v) => v.id === restoreVersionId);
          if (!target) return null;
          return (
            <TaxonomyReasonModal
              open
              title={t('taxonomy.taxonomyListDetail.restoreVersionTitle', {
                version: target.versionNumber,
              })}
              description={t('taxonomy.taxonomyListDetail.restoreVersionDescription', {
                draft: head.versionNumber + 1,
                source: target.versionNumber,
                head: head.versionNumber,
              })}
              /* Rolling a vocabulary back changes what every FOLLOWING schema
                 resolves, so the blast radius belongs here as much as on publish. */
              extra={
                <ListImpactSummary impact={impactQuery.data} isLoading={impactQuery.isLoading} />
              }
              confirmLabel={t('taxonomy.taxonomyListDetail.restoreVersion')}
              isSubmitting={addVersion.isPending}
              onCancel={() => setRestoreVersionId(null)}
              onConfirm={(reason) =>
                addVersion.mutate(
                  { expectedVersion: list.version, parentVersionId: restoreVersionId, reason },
                  {
                    onSuccess: () => {
                      setRestoreVersionId(null);
                      setSelectedVersionId(null);
                    },
                  },
                )
              }
            />
          );
        })()}

      {deleteOpen && (
        <FormDialog
          open
          onOpenChange={(next) => !next && setDeleteOpen(false)}
          title={t('taxonomy.taxonomyListDetail.deleteListTitle', { name: list.name })}
          className="flex flex-col sm:!max-w-[28rem]"
        >
          <FormDialog.Body className="flex flex-col gap-3">
            {blockersQuery.isError ? (
              <p className="text-sm text-muted-foreground">
                {t('taxonomy.deleteListDialog.couldNotCheckUsage')}
              </p>
            ) : blockersQuery.isFetching ? (
              <p className="text-sm text-muted-foreground">
                {t('taxonomy.deleteListDialog.checkingUsage')}
              </p>
            ) : blockers.length > 0 ? (
              <>
                <p className="text-sm text-muted-foreground">
                  {t('taxonomy.taxonomyListDetail.schemasStillBind', {
                    count: blockers.length,
                  })}
                </p>
                <ul className="flex flex-col gap-0.5">
                  {blockers.map((sch) => (
                    <li key={sch.taxonomyId} className="truncate text-xs text-muted-foreground">
                      {sch.taxonomyName}
                      <span className="ml-1 opacity-70">({sch.fieldNames.join(', ')})</span>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                {t('taxonomy.taxonomyListDetail.noSchemaUsesList', {
                  count: head.rows.length,
                })}
              </p>
            )}
          </FormDialog.Body>
          <FormDialog.Footer
            primary={{
              label: t('taxonomy.taxonomyListDetail.deleteList'),
              onClick: () => remove.mutate(listId, { onSuccess: onClose }),
              disabled:
                blockers.length > 0 ||
                remove.isPending ||
                blockersQuery.isFetching ||
                blockersQuery.isError,
              loading: remove.isPending,
            }}
            secondary={{
              label: t('taxonomy.taxonomyListDetail.cancel'),
              onClick: () => setDeleteOpen(false),
            }}
          />
        </FormDialog>
      )}

      <ListHistoryDrawer
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        listId={listId}
        currentUserId={currentUser?.id}
        currentUserName={currentUser?.name}
        onViewVersion={(versionId) => {
          setSelectedVersionId(versionId === head.id ? null : versionId);
          setHistoryOpen(false);
        }}
      />
    </TooltipProvider>
  );
}

TaxonomyListEditor.displayName = 'TaxonomyListEditor';

interface ListRowsTableProps {
  columns: ListColumns;
  rows: TaxonomyListRow[];
  /** Only the head draft is editable; every other version renders frozen. */
  editable: boolean;
  onSetCell: (rowId: string, columnId: string, value: string) => void;
  onRemoveRow: (rowId: string) => void;
  onCellPaste: (
    e: React.ClipboardEvent<HTMLInputElement>,
    rowIndex: number,
    columnId: string,
  ) => void;
  columnLabel: (column: TaxonomyListColumn) => string;
}

/**
 * The vocabulary table, windowed past {@link VIRTUALIZE_THRESHOLD} — the same
 * threshold and fixed row height the schema grid uses.
 *
 * Every cell is a controlled input and the seeded ISO vocabularies are large
 * (Countries 250 rows, Languages 183), so without windowing opening one mounts
 * hundreds of inputs and every keystroke re-renders all of them. Disabled
 * inputs count: those lists ship published, so read-only viewers paid it too.
 */
function ListRowsTable(props: ListRowsTableProps) {
  const { t } = useTranslation('knowledge');
  const { rows, editable, columnLabel } = props;
  const { value, code, extras } = props.columns;
  const columns = [value, code, ...extras].filter((c): c is TaxonomyListColumn => c !== undefined);

  const shouldVirtualize = rows.length > VIRTUALIZE_THRESHOLD;
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollMargin, setScrollMargin] = useState(0);

  // Rows sit below the sticky <thead>; scrollMargin is that offset, so the
  // virtualizer's row coordinates line up with the scroller.
  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller || !shouldVirtualize) return;
    const measure = () => {
      // eslint-disable-next-line @typescript-eslint/no-deprecated -- `tbody` is a current tag; the rule cannot pin the non-deprecated overload
      const tbody = scroller.querySelector<HTMLTableSectionElement>('tbody');
      if (!tbody) return;
      const next =
        tbody.getBoundingClientRect().top -
        scroller.getBoundingClientRect().top +
        scroller.scrollTop;
      setScrollMargin((prev) => (Math.abs(prev - next) > 0.5 ? next : prev));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(scroller);
    return () => ro.disconnect();
  }, [shouldVirtualize, columns.length, rows.length]);

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 12,
    scrollMargin,
    enabled: shouldVirtualize,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();
  const rowWindow =
    shouldVirtualize && virtualItems.length > 0
      ? {
          indexes: virtualItems.map((v) => v.index),
          paddingTop: Math.max(0, virtualItems[0].start - scrollMargin),
          paddingBottom: Math.max(
            0,
            rowVirtualizer.getTotalSize() -
              (virtualItems[virtualItems.length - 1].end - scrollMargin),
          ),
        }
      : null;

  const totalCols = columns.length + (editable ? 1 : 0);

  return (
    <div
      ref={scrollRef}
      className="min-h-0 flex-1 overflow-auto rounded-lg border border-border-light"
    >
      <table className="w-full border-separate border-spacing-0 text-sm">
        <thead className="sticky top-0 z-10">
          <tr>
            {columns.map((col) => (
              <th
                key={col.id}
                scope="col"
                className="border-b border-l border-border-light bg-surface px-3 py-2 text-left text-xs font-medium text-foreground-subtle first:border-l-0"
              >
                {columnLabel(col)}
              </th>
            ))}
            {editable && (
              <th className="w-10 border-b border-l border-border-light bg-surface px-2 py-2">
                <span className="sr-only">{t('taxonomy.taxonomyListDetail.removeRow')}</span>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={totalCols}
                className="px-3 py-8 text-center text-sm text-muted-foreground"
              >
                {t('taxonomy.taxonomyListDetail.noOptionsYet')}{' '}
                {editable && t('taxonomy.taxonomyListDetail.addRowStartBuildingVocabulary')}
              </td>
            </tr>
          ) : (
            <>
              {/* A tall spacer row stands in for the off-screen rows above the
                  visible slice, the way the schema grid does it. */}
              {rowWindow && rowWindow.paddingTop > 0 && (
                <tr aria-hidden>
                  <td colSpan={totalCols} style={{ height: rowWindow.paddingTop, padding: 0 }} />
                </tr>
              )}
              {(rowWindow ? rowWindow.indexes : rows.map((_, i) => i)).map((rowIndex) => {
                const row = rows[rowIndex];
                return (
                  <tr key={row.id} className="group">
                    {columns.map((col) => {
                      const spaced =
                        col === code ? rowCodeWithSpace(row, props.columns) : undefined;
                      return (
                        <td
                          key={col.id}
                          className="relative border-b border-l border-border-light p-0 first:border-l-0"
                        >
                          <Input
                            value={row.cells[col.id] ?? ''}
                            maxLength={MAX_LIST_CELL_LENGTH}
                            disabled={!editable}
                            onChange={(e) => props.onSetCell(row.id, col.id, e.target.value)}
                            // Absolute row index, so a paste spills down from the
                            // real row rather than the top of the window.
                            onPaste={(e) => props.onCellPaste(e, rowIndex, col.id)}
                            className={`h-9 rounded-none border-0 bg-transparent ${spaced ? 'pr-7' : ''}`}
                            aria-label={t('taxonomy.taxonomyListDetail.columnValue', {
                              name: columnLabel(col),
                            })}
                          />
                          {spaced && (
                            <span className="absolute inset-y-0 right-xs flex items-center">
                              <ProblemTriangle
                                warning
                                message={`${
                                  spaced.fromValue
                                    ? t('taxonomy.taxonomyListDetail.valueUsedAsCodeHasSpace', {
                                        value: spaced.code,
                                      })
                                    : t('taxonomy.taxonomyListDetail.codeHasSpace', {
                                        code: spaced.code,
                                      })
                                } ${t('taxonomy.taxonomyListDetail.codeSpaceHint')}`}
                              />
                            </span>
                          )}
                        </td>
                      );
                    })}
                    {editable && (
                      <td className="border-b border-l border-border-light px-2 text-center">
                        <button
                          type="button"
                          aria-label={t('taxonomy.taxonomyListDetail.removeRow')}
                          onClick={() => props.onRemoveRow(row.id)}
                          className="text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
                        >
                          <Trash2 className="size-3.5" />
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
              {rowWindow && rowWindow.paddingBottom > 0 && (
                <tr aria-hidden>
                  <td colSpan={totalCols} style={{ height: rowWindow.paddingBottom, padding: 0 }} />
                </tr>
              )}
            </>
          )}
        </tbody>
      </table>
    </div>
  );
}

ListRowsTable.displayName = 'ListRowsTable';
